import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coins, Loader2, ExternalLink, Copy, Check, Clock, BadgeCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { CreditPackBreakdown } from "@/components/CreditPackBreakdown";

const STATUS_BADGE = {
  awaiting_review: { label: "Awaiting review", cls: "text-amber-300 bg-amber-300/10 border-amber-300/30", Icon: Clock },
  paid: { label: "Credited", cls: "text-emerald-300 bg-emerald-300/10 border-emerald-300/30", Icon: BadgeCheck },
  rejected: { label: "Not approved", cls: "text-red-300 bg-red-300/10 border-red-300/30", Icon: XCircle },
};

// Manual Venmo top-up: player pays the operator's handle, then an admin credits them.
export function VenmoTopUp({ bare = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [payerHandle, setPayerHandle] = useState("");
  const [ticket, setTicket] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data: cfg } = useQuery({ queryKey: ["venmo-config"], queryFn: async () => (await api.get("/payments/venmo/config")).data });
  const { data: packages } = useQuery({ queryKey: ["credit-packages"], queryFn: async () => (await api.get("/credit-packages")).data, staleTime: 0, refetchInterval: 30_000 });
  const { data: history, refetch } = useQuery({
    queryKey: ["payment-history"],
    queryFn: async () => (await api.get("/payments/history")).data,
    enabled: !!user,
  });

  if (cfg && cfg.enabled === false) return null;

  const venmoRequests = (history || []).filter((t) => t.provider === "venmo");

  const submit = async () => {
    if (!user) { window.location.href = "/login"; return; }
    if (!picked) { toast.error("Pick a credit pack first."); return; }
    setBusy(true);
    try {
      const res = await api.post("/payments/venmo/request", { package_id: picked, payer_handle: payerHandle });
      setTicket(res.data);
      refetch();
      toast.success("Request logged — now send the payment on Venmo.");
    } catch (e) {
      toast.error(apiError(e, "Couldn't create the request"));
    } finally {
      setBusy(false);
    }
  };

  const copyNote = () => {
    navigator.clipboard.writeText(ticket.note);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const list = packages || [];

  return (
    <div data-testid="venmo-topup" className={bare ? "relative" : "hud-pill rounded-2xl p-5 sm:p-6 relative overflow-hidden"}>
      {!bare && (
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-[#3D95CE]/15 border border-[#3D95CE]/40 flex items-center justify-center">
          <span className="font-display text-[#3D95CE] text-2xl leading-none">V</span>
        </div>
        <div>
          <h2 className="font-display text-2xl tracking-[0.04em] text-white">TOP UP WITH VENMO</h2>
          <p className="text-white/50 text-sm">
            Pay <span className="text-[#3D95CE] font-semibold">@{cfg?.handle || "…"}</span> on Venmo, then we add your credits after a quick review.
          </p>
        </div>
      </div>
      )}

      {!ticket ? (
        <>
          <div className="grid gap-3 mt-4 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((p) => (
              <button
                key={p.id}
                data-testid={`venmo-pack-${p.id}`}
                onClick={() => setPicked(p.id)}
                className={`text-left relative rounded-xl border p-4 flex flex-col transition-all ${picked === p.id ? "border-[#3D95CE] bg-[#3D95CE]/[0.08]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"}`}
              >
                {picked === p.id && <Check className="absolute top-2 right-2 h-4 w-4 text-[#3D95CE]" />}
                <div data-testid={`venmo-pack-name-${p.id}`} className="font-display text-lg text-white tracking-[0.04em]">{p.name}</div>
                <div className="flex items-center gap-1.5 mt-1 text-amber-300 font-mono">
                  <Coins className="h-4 w-4 shrink-0" /> <span data-testid={`venmo-pack-total-${p.id}`} className="text-xl">{p.credits.toLocaleString()}</span>
                </div>
                <div data-testid={`venmo-pack-price-${p.id}`} className="font-mono text-[13px] text-white/70 mt-1">${p.price_usd.toFixed(2)}</div>
                <CreditPackBreakdown pack={p} prefix={`venmo-pack-${p.id}`} />
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch">
            <Input
              data-testid="venmo-payer-handle"
              value={payerHandle}
              onChange={(e) => setPayerHandle(e.target.value)}
              placeholder="Your Venmo username (optional, helps us match it)"
              className="bg-[hsl(220_18%_12%)] border-white/10 h-11"
            />
            <Button
              data-testid="venmo-request-button"
              onClick={submit}
              disabled={busy || !picked}
              className="bg-[#3D95CE] text-white hover:bg-[#338ac0] h-11 px-6 disabled:opacity-40 shrink-0"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request top-up"}
            </Button>
          </div>
        </>
      ) : (
        <div data-testid="venmo-ticket" className="mt-4 rounded-xl border border-[#3D95CE]/30 bg-[#3D95CE]/[0.06] p-4 space-y-3">
          <div className="text-white font-semibold flex items-center gap-2">
            <Coins className="h-4 w-4 text-amber-300" /> {ticket.credits.toLocaleString()} credits · ${ticket.amount_usd.toFixed(2)}
          </div>
          <ol className="text-sm text-white/75 space-y-2 list-decimal list-inside">
            <li>
              Send <span className="text-white font-semibold">${ticket.amount_usd.toFixed(2)}</span> to{" "}
              <span className="text-[#3D95CE] font-semibold">@{ticket.handle}</span> on Venmo.
            </li>
            <li className="flex flex-wrap items-center gap-2">
              Include this note so we can match it:
              <span className="font-mono text-amber-200 bg-black/30 rounded px-2 py-0.5">{ticket.note}</span>
              <button data-testid="venmo-copy-note" onClick={copyNote} className="text-white/50 hover:text-white">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </li>
            <li>We review and add your credits — you'll get a notification.</li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <a href={ticket.pay_url} target="_blank" rel="noreferrer" data-testid="venmo-open-pay">
              <Button className="bg-[#3D95CE] text-white hover:bg-[#338ac0]"><ExternalLink className="h-4 w-4 mr-1.5" /> Open Venmo to pay</Button>
            </a>
            <Button variant="ghost" className="text-white/60" onClick={() => { setTicket(null); setPicked(null); }} data-testid="venmo-new-request">
              Make another request
            </Button>
          </div>
        </div>
      )}

      {venmoRequests.length > 0 && (
        <div className="mt-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Your Venmo requests</div>
          <div className="space-y-1.5">
            {venmoRequests.slice(0, 6).map((t) => {
              const b = STATUS_BADGE[t.payment_status] || STATUS_BADGE.awaiting_review;
              return (
                <div key={t.order_id} data-testid={`venmo-history-${t.order_id}`} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
                  <span className="text-white/70">{(t.credits || 0).toLocaleString()} credits · ${Number(t.amount_usd || 0).toFixed(2)}</span>
                  <span className={`inline-flex items-center gap-1 font-mono text-[10px] rounded-full border px-2 py-0.5 ${b.cls}`}>
                    <b.Icon className="h-3 w-3" /> {b.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
